import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import apiService from '../services/api';

interface Usuario {
  username: string;
  email: string;
  nombre: string;
  apellido: string;
  rol: 'administrador' | 'operador';
  empresasAsignadas?: string[];
  activo: boolean;
  fechaCreacion: string;
}

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [empresas, setEmpresas] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    nombre: '',
    apellido: '',
    rol: 'operador' as 'administrador' | 'operador',
    empresasAsignadas: [] as string[],
    password: '',
  });
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    username: '',
    password: '',
  });

  useEffect(() => {
    const loadData = async () => {
      await loadUsuarios();
      await loadEmpresas();
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsuarios = async () => {
    setLoading(true);
    try {
      const data = await apiService.getUsuarios();
      setUsuarios(data.usuarios || []);
    } catch (err: any) {
      showSnackbar(err.response?.data?.mensaje || 'Error al cargar usuarios', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadEmpresas = async () => {
    try {
      const data = await apiService.getEmpresas();
      setEmpresas(data.empresas || []);
    } catch (err: any) {
      console.error('Error al cargar empresas:', err);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (usuario?: Usuario) => {
    if (usuario) {
      setEditMode(true);
      setSelectedUsername(usuario.username);
      setFormData({
        username: usuario.username,
        email: usuario.email,
        nombre: usuario.nombre,
        apellido: usuario.apellido,
        rol: usuario.rol,
        empresasAsignadas: usuario.empresasAsignadas || [],
        password: '',
      });
    } else {
      setEditMode(false);
      setSelectedUsername('');
      setFormData({
        username: '',
        email: '',
        nombre: '',
        apellido: '',
        rol: 'operador',
        empresasAsignadas: [],
        password: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      username: '',
      email: '',
      nombre: '',
      apellido: '',
      rol: 'operador',
      empresasAsignadas: [],
      password: '',
    });
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await apiService.updateUsuario(selectedUsername, {
          nombre: formData.nombre,
          apellido: formData.apellido,
          rol: formData.rol,
          empresasAsignadas: formData.rol === 'operador' ? formData.empresasAsignadas : [],
        });
        showSnackbar('Usuario actualizado exitosamente', 'success');
      } else {
        if (!formData.password) {
          showSnackbar('La contraseña es requerida', 'error');
          return;
        }
        if (formData.rol === 'operador' && formData.empresasAsignadas.length === 0) {
          showSnackbar('Los operadores deben tener al menos una empresa asignada', 'error');
          return;
        }
        await apiService.createUsuario(formData);
        showSnackbar('Usuario creado exitosamente', 'success');
      }
      handleCloseDialog();
      loadUsuarios();
    } catch (err: any) {
      showSnackbar(err.response?.data?.mensaje || 'Error al guardar usuario', 'error');
    }
  };

  const handleDelete = async (username: string) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) {
      return;
    }
    try {
      await apiService.deleteUsuario(username);
      showSnackbar('Usuario eliminado exitosamente', 'success');
      loadUsuarios();
    } catch (err: any) {
      showSnackbar(err.response?.data?.mensaje || 'Error al eliminar usuario', 'error');
    }
  };

  const handleOpenPasswordDialog = (username: string) => {
    setPasswordData({ username, password: '' });
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setPasswordData({ username: '', password: '' });
  };

  const handleResetPassword = async () => {
    if (!passwordData.password) {
      showSnackbar('La contraseña es requerida', 'error');
      return;
    }
    try {
      await apiService.resetPassword(passwordData.username, passwordData.password);
      showSnackbar('Contraseña actualizada exitosamente', 'success');
      handleClosePasswordDialog();
    } catch (err: any) {
      showSnackbar(err.response?.data?.mensaje || 'Error al resetear contraseña', 'error');
    }
  };

  const columns: GridColDef[] = [
    { field: 'username', headerName: 'Usuario', width: 150 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    { field: 'nombre', headerName: 'Nombre', flex: 1, minWidth: 150 },
    { field: 'apellido', headerName: 'Apellido', flex: 1, minWidth: 150 },
    {
      field: 'rol',
      headerName: 'Rol',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value === 'administrador' ? 'Administrador' : 'Operador'}
          color={params.value === 'administrador' ? 'primary' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'empresasAsignadas',
      headerName: 'Empresas',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        if (params.row.rol === 'administrador') {
          return <Chip label="Todas" color="success" size="small" />;
        }
        return params.value?.length || 0;
      },
    },
    {
      field: 'activo',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenDialog(params.row)}
            title="Editar"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="secondary"
            onClick={() => handleOpenPasswordDialog(params.row.username)}
            title="Resetear contraseña"
          >
            <VpnKeyIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.username)}
            title="Eliminar"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Usuarios
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Usuario
        </Button>
      </Box>

      <Card>
        <CardContent>
          <DataGrid
            rows={usuarios}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.username}
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      {/* Dialog para crear/editar usuario */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Usuario"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              disabled={editMode}
              required
              fullWidth
              helperText="Nombre de usuario para iniciar sesión (sin espacios)"
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={editMode}
              required
              fullWidth
            />
            <TextField
              label="Nombre"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Apellido"
              value={formData.apellido}
              onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>Rol</InputLabel>
              <Select
                value={formData.rol}
                label="Rol"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    rol: e.target.value as 'administrador' | 'operador',
                    empresasAsignadas: e.target.value === 'administrador' ? [] : formData.empresasAsignadas,
                  })
                }
              >
                <MenuItem value="administrador">Administrador</MenuItem>
                <MenuItem value="operador">Operador</MenuItem>
              </Select>
            </FormControl>

            {formData.rol === 'operador' && (
              <FormControl fullWidth required>
                <InputLabel>Empresas Asignadas</InputLabel>
                <Select
                  multiple
                  value={formData.empresasAsignadas}
                  label="Empresas Asignadas"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      empresasAsignadas: e.target.value as string[],
                    })
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((ruc) => {
                        const empresa = empresas.find((e) => e.ruc === ruc);
                        return (
                          <Chip
                            key={ruc}
                            label={empresa?.nombreComercial || ruc}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {empresas.map((empresa) => (
                    <MenuItem key={empresa.ruc} value={empresa.ruc}>
                      {empresa.nombreComercial} ({empresa.ruc})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!editMode && (
              <TextField
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Mínimo 8 caracteres, debe incluir mayúsculas, minúsculas y números"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para resetear contraseña */}
      <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Resetear Contraseña</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Usuario: {passwordData.username}
            </Typography>
            <TextField
              label="Nueva Contraseña"
              type={showPassword ? 'text' : 'password'}
              value={passwordData.password}
              onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText="Mínimo 8 caracteres, debe incluir mayúsculas, minúsculas y números"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>Cancelar</Button>
          <Button onClick={handleResetPassword} variant="contained">
            Actualizar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
